/**
 * wipPants — which TYPE of pants one WIP is, chosen by a person.
 *
 * Mikko, 2026-10-02, relaying HR: "better to have detailed edit in Types of
 * Pants so pants can edit if it's Regular, Jogger, Cargo Pants and so on,
 * because some WIP and SKU are not all the same type of pants — inside WIP
 * which type of pants to be edited."
 *
 * Every set is paid twice, a design band for the top and a PANTS band for the
 * trousers (payoutDraft.ts). Until now the pants band came from the DESIGN
 * (`pantsMap.ts`: SS028 -> Cargo 6-pocket, ...), one answer per design. But two
 * WIPs of the same design are not always the same trousers — one is a regular
 * 4-pocket, the next a jogger — and the pants bands are priced apart (P1 REGULAR
 * 4-POCKET ₱60 ... P13 ALTA OVER ALL ₱175). So the choice is stored per WIP and
 * read BEFORE the design's: a person looking at this WIP decided.
 *
 * KEYED ON THE WIP, VALUE IS A RATE KEY. A WIP inspected in three passes is one
 * set of trousers, so all three passes take the one answer. The value is the
 * rate card's own `rateKey` (unique by construction — its `code` is not), and
 * only a PANTS-group row with a rate is honoured: a stale or hand-edited key
 * falls back to the design's band rather than pricing nothing.
 *
 * Nothing about a paid payout moves: a payout freezes the lines it was paid on,
 * and the run checks the paid set before it prices anything.
 *
 * Stored as one JSON map in `iSyn2_SubconPayrollSettings`, the same way the
 * WIP plans and the pants-by-design choices are. No new list, no new column,
 * so no Migration Valve step. Pure and I/O-free.
 */
import type { IRateRow } from "./rateCard";
import type { IWoLogBatch } from "../mfg-workcenters/bqc1/woLogStorage";
import { pantsRowFor, type StoredPantsMap } from "./pantsMap";

/** The settings-list key the stored choices live under. */
export const WIP_PANTS_KEY = "wipPants";

/** WIP id -> the pants band's rateKey a person chose. */
export type StoredWipPants = Record<string, string>;

/** Parse the stored blob. Never throws; an unreadable row means nothing was chosen. */
export function parseStoredWipPants(text: string | null | undefined): StoredWipPants {
  if (!text) return {};
  try {
    const raw: unknown = JSON.parse(text);
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    const out: StoredWipPants = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      const wipId = String(k ?? "").trim();
      const key = String(v ?? "").trim();
      if (wipId && key) out[wipId] = key;
    }
    return out;
  } catch {
    return {};
  }
}

/** The bands a person can choose from: every priced PANTS row, in card order. */
export function pantsBandOptions(card: readonly IRateRow[]): IRateRow[] {
  return card.filter(r => r.group === "PANTS" && r.rate !== null);
}

/** The band a stored choice names — only if it is still a priced PANTS row. */
export function chosenPantsRow(
  wipId: string, stored: StoredWipPants | undefined, card: readonly IRateRow[],
): IRateRow | null {
  const key = stored?.[wipId];
  if (!key) return null;
  const row = card.find(r => r.rateKey === key);
  return row && row.group === "PANTS" && row.rate !== null ? row : null;
}

// ─── THE W.O. LOG'S PANTS DESIGN, AS THE DEFAULT ────────────────────────────
//
// Mikko, 2026-10-02: "the default value here in the pants type dropdown should
// be its design type, selecting pants would be optional ... in BQC1 W.O. Logs
// Pants type here, it would help to decide which pants belong."
//
// Every Cutting J.O. Log row carries the PANTS DESIGN the cutter was told to
// cut (DOUBLE WELT, CARGO ZIPPLY, TAPERED ZIPPER, LAP TAPERED ...). That is the
// best evidence there is for which trousers a WIP is, because it was written
// BEFORE they were cut, per WIP, not per design. So the order is:
//
//   1. what a person chose for THIS WIP      (the dropdown — optional)
//   2. what the W.O. Log says for this WIP  (below — when it names a band)
//   3. the design's band                     (pantsMap: evidenced / category)
//
// ONLY WORDING THAT NAMES A BAND IS MAPPED. The log's words are free text and
// the bands are priced apart (₱60 to ₱175), so a guess is invented money. Each
// rule below is a phrase the rate card's OWN band name carries, and the 7 Sep
// 2026 payslips agree with them (041/044/046 jogger designs paid ₱130
// invisible/tapered; 031B ₱100 zip ply; 028/029/032/033/035 ₱110 zipper). What
// is NOT mapped is left to the design's band and says so on screen:
//
//   ALTA ...      the ALTA / ELITE PRO bands are priced by CATEGORY, not by the
//                 words on a log ("ALTA DOUBLE CARGO" is not a double-welt cargo)
//   PRIMO CARGO   no band is named PRIMO; the ladder for it runs ₱60 to ₱100
//   JOGGER        P2, P4 and P7 are all joggers at ₱65 / ₱75 / ₱120
//   CARGO ZIPPER  P6 and P7 both say zipper, ₱110 and ₱120
//
// A person picks for those, once, per WIP.

/** A log's pants wording -> the PANTS band CODE it names, or null when it names none. */
export function pantsCodeFromLogText(text: string | null | undefined): string | null {
  const t = String(text ?? "").toUpperCase().replace(/\s+/g, " ").trim();
  if (!t) return null;
  if (/\bALTA\b/.test(t)) return null;                 // priced by category, never by the log's words
  if (/INVISIBLE|TAPER|\bLAP\b/.test(t)) return "P8";  // CARGO/JOGGER 6-P w/ INVISIBLE / TAPERED / LAP
  if (/ZIP ?PLY/.test(t)) return "P5";                   // CARGO 6-POCKET (2-in) w/ ZIP PLY
  if (/DOUBLE WELT/.test(t)) return "P6";                // CARGO 6-POCKET (2-in) w/ ZIPPER or DOUBLE WELT
  if (/PIPING/.test(t)) return "P4";                     // CARGO 6-POCKET (1-in) w/ PIPING / JOGGER
  if (/\bREGULAR\b/.test(t)) return "P1";               // REGULAR 4-POCKET
  return null;
}

/** The priced PANTS row a log's wording names, or null. */
export function woLogPantsRow(text: string | null | undefined, card: readonly IRateRow[]): IRateRow | null {
  const code = pantsCodeFromLogText(text);
  if (!code) return null;
  return card.find(r => r.group === "PANTS" && r.code === code && r.rate !== null) ?? null;
}

/**
 * WIP -> the pants design the Cutting J.O. Log gave it, from every imported
 * log; the newest import wins, as with the plans (wipPlan.planByWipFromBatches).
 * Rows with no pants wording are left out, not stored as "".
 */
export function pantsTextByWipFromBatches(batches: readonly IWoLogBatch[]): Map<string, string> {
  const at = new Map<string, string>();
  const out = new Map<string, string>();
  for (const b of batches) {
    const stamp = String(b.importedAt ?? "");
    for (const r of b.rows ?? []) {
      const wipId = String(r.wipId ?? "").trim();
      const text = String(r.pants ?? "").trim();
      if (!wipId) continue;
      const seen = at.get(wipId);
      if (seen !== undefined && seen > stamp) continue;
      at.set(wipId, stamp);
      if (text) out.set(wipId, text); else out.delete(wipId);
    }
  }
  return out;
}

/** What a WIP's pants band is when nobody has chosen one, and where that came from. */
export interface IPantsDefault {
  row: IRateRow | null;
  /** "wo-log": the log named a band. "design": the design's band. "none": neither. */
  source: "wo-log" | "design" | "none";
  /** The log's own words for this WIP, whether or not they named a band. */
  logText: string;
}

export function defaultPantsFor(o: {
  wipId: string; design: string | undefined; woLogText: string | undefined;
  stored: StoredPantsMap | undefined; card: readonly IRateRow[];
}): IPantsDefault {
  const logText = String(o.woLogText ?? "").trim();
  const byDesign = pantsRowFor(o.design, o.stored ?? {}, o.card);
  const fromLog = woLogPantsRow(logText, o.card);
  if (fromLog) return { row: fromLog, source: "wo-log", logText };
  return { row: byDesign, source: byDesign ? "design" : "none", logText };
}
